import { format } from 'date-fns'
import { USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import {
  IFuelAuthorizedDrawerGroup,
  IFuelDatedDemoRow,
  IFuelDrumFloat,
  IFuelOpsBurnRate,
  IFuelOpsConsumptionPoint,
  IFuelOpsDelivery,
  IFuelOpsDispenseRow,
  IFuelOpsKpis,
  IFuelOpsLossRow,
  IFuelOpsSupplierRow,
  IFuelSupplierClaim,
  IFuelSupplierCommercials,
  IFuelSupplierOpenClaimsSummary,
} from 'types/fuelOps.interface'
import { IFuelDeliveryRecord } from 'types/fuelDeliveries.interface'
import { IFuelAlertResponse } from 'types/api/fuelManagementApi.interface'
import { currentWacPerL } from './fuelPricing'
import { fuelReconDateISO } from './fuelTanksDemoData'

export const FUEL_DIESEL_PRICE_GHS = currentWacPerL()

export const lastDeliveryPricePerL = 12.55

export const FUEL_DELIVERY_TOLERANCE_PCT = 0.5

export const FUEL_WRITE_OFF_APPROVAL_THRESHOLD_GHS = 50000

export const formatGhs = (value: number): string =>
  'GH₵ ' + new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value)

export const formatGhsCompact = (value: number): string =>
  value >= 1000000
    ? 'GH₵ ' + (value / 1000000).toFixed(2).replace(/0$/, '') + 'M'
    : formatGhs(value)

export const FUEL_OPS_KPIS: IFuelOpsKpis = {
  dieselPriceGhs: FUEL_DIESEL_PRICE_GHS,
  stockLiters: 46954,
  stockValueGhs: 582230,
  burnLitersPerDay: 4100,
  burnMonthGhs: 1520000,
  spendMonthGhs: 1520000,
  spendLastMonthGhs: 1410000,
  intensityLitersPerTonne: 2.2,
}

export const FUEL_STOCK_VALUATION_LINE = `Stock valued at weighted average GH₵ ${FUEL_DIESEL_PRICE_GHS.toFixed(
  2,
)}/L · last delivery GH₵ ${lastDeliveryPricePerL.toFixed(2)}/L`

export const FUEL_UNVERIFIED_DELIVERY_NOTE =
  'Received before the delivery verification process was introduced.'

const DEMO_DELIVERIES: IFuelDeliveryRecord[] = [
  {
    id: 1,
    dateLabel: '1 Jul',
    dateISO: fuelReconDateISO(4),
    liters: 9000,
    costGhs: 112950,
    supplier: 'Sabel Fuels',
    tank: 'Tank A — Main Diesel',
    fuelType: 'Diesel',
    pricePerL: 12.55,
    waybillRef: 'WB-2071',
    waybillLitres: 9000,
    tankDipBeforeL: 12140,
    tankDipAfterL: 21100,
    measuredInL: 8960,
    varianceL: -40,
    variancePct: -0.4,
    sealNumbers: ['S-4471', 'S-4472'],
    sealsIntact: true,
    verifiedBy: 'S. Banda',
    verificationStatus: 'VERIFIED',
    verificationNote: '−40 L within the 0.5% receiving tolerance — no claim.',
    receivedBy: 'S. Banda',
  },
  {
    id: 2,
    dateLabel: '24 Jun',
    dateISO: fuelReconDateISO(11),
    liters: 32150,
    costGhs: 407550,
    supplier: 'Sabel Fuels',
    tank: 'Tank A — Main Diesel',
    fuelType: 'Diesel',
    pricePerL: 12.35,
    waybillRef: 'WB-1988',
    waybillLitres: 33000,
    tankDipBeforeL: 4850,
    tankDipAfterL: 37000,
    measuredInL: 32150,
    varianceL: -850,
    variancePct: -2.6,
    sealNumbers: ['S-4402', 'S-4403'],
    sealsIntact: false,
    verifiedBy: 'S. Banda',
    verificationStatus: 'SHORT',
    verificationNote: 'Seal S-4403 did not match the waybill record.',
    claimNote: 'GH₵ 10,498 supplier claim — Sabel Fuels',
    receivedBy: 'S. Banda',
  },
  {
    id: 3,
    dateLabel: '17 Jun',
    dateISO: fuelReconDateISO(18),
    liters: 10500,
    costGhs: 129675,
    supplier: 'Sabel Fuels',
    tank: 'Tank B — Genset',
    fuelType: 'Diesel',
    pricePerL: 12.35,
    waybillRef: 'WB-1911',
    waybillLitres: 10500,
    tankDipBeforeL: 2210,
    tankDipAfterL: 12680,
    measuredInL: 10470,
    varianceL: -30,
    variancePct: -0.3,
    sealNumbers: ['S-4361'],
    sealsIntact: true,
    verifiedBy: 'K. Mensah',
    verificationStatus: 'VERIFIED',
    receivedBy: 'K. Mensah',
  },
  {
    id: 4,
    dateLabel: '12 May',
    dateISO: fuelReconDateISO(54),
    liters: 8000,
    costGhs: 96800,
    supplier: 'Sabel Fuels',
    tank: 'Tank A — Main Diesel',
    fuelType: 'Diesel',
    pricePerL: 12.1,
    waybillRef: 'WB-1740',
    waybillLitres: 8000,
    tankDipBeforeL: 9310,
    tankDipAfterL: 17310,
    measuredInL: 8000,
    varianceL: 0,
    variancePct: 0,
    sealNumbers: ['S-4118', 'S-4119'],
    sealsIntact: true,
    verifiedBy: 'S. Banda',
    verificationStatus: 'VERIFIED',
    receivedBy: 'S. Banda',
  },
  {
    id: 5,
    dateLabel: '28 Apr',
    dateISO: fuelReconDateISO(68),
    liters: 12000,
    costGhs: 145200,
    supplier: 'GOIL Bulk',
    tank: 'Tank A — Main Diesel',
    fuelType: 'Diesel',
    pricePerL: 12.1,
    waybillRef: null,
    waybillLitres: null,
    tankDipBeforeL: null,
    tankDipAfterL: null,
    measuredInL: null,
    varianceL: null,
    variancePct: null,
    sealNumbers: [],
    sealsIntact: null,
    verifiedBy: null,
    verificationStatus: 'UNVERIFIED',
    verificationNote: FUEL_UNVERIFIED_DELIVERY_NOTE,
    receivedBy: null,
  },
]

const DEMO_CONSUMPTION_14D: IFuelOpsConsumptionPoint[] = [
  { name: '19 Jun', value: 3980 },
  { name: '20 Jun', value: 4120 },
  { name: '21 Jun', value: 4210 },
  { name: '22 Jun', value: 3890 },
  { name: '23 Jun', value: 4060 },
  { name: '24 Jun', value: 4180 },
  { name: '25 Jun', value: 3950 },
  { name: '26 Jun', value: 4230 },
  { name: '27 Jun', value: 4040 },
  { name: '28 Jun', value: 5760 },
  { name: '29 Jun', value: 4150 },
  { name: '30 Jun', value: 3920 },
  { name: '1 Jul', value: 4080 },
  { name: '2 Jul', value: 4110 },
]

export const FUEL_CONSUMPTION_SPIKE_NOTE =
  'Spike 28 Jun — pre-blast double haul cycle, logged and explained. Baseline ~4,100 L/day.'

const DEMO_DISPENSES: IFuelOpsDispenseRow[] = [
  {
    id: 1,
    dayLabel: 'Yesterday',
    dateISO: fuelReconDateISO(1),
    time: '06:05',
    machine: 'Truck 03',
    operator: 'K. Mensah',
    liters: 220,
    smuHours: 4210.6,
    tank: 'Tank A',
    dispenseType: 'MACHINE',
    authStatus: 'AUTHORIZED',
  },
  {
    id: 2,
    dayLabel: 'Yesterday',
    dateISO: fuelReconDateISO(1),
    time: '09:12',
    machine: 'Truck 05',
    operator: 'J. Owusu',
    liters: 400,
    smuHours: 6118.4,
    tank: 'Tank A',
    flag: { tone: 'warning', label: 'Full-tank fill' },
    dispenseType: 'MACHINE',
    authStatus: 'AUTHORIZED',
  },
  {
    id: 3,
    dayLabel: 'Yesterday',
    dateISO: fuelReconDateISO(1),
    time: '12:30',
    machine: 'Genset 2',
    operator: 'S. Adjei',
    liters: 150,
    smuHours: 12480.2,
    tank: 'Tank B',
    dispenseType: 'MACHINE',
    authStatus: 'AUTHORIZED',
  },
  {
    id: 4,
    dayLabel: 'Yesterday',
    dateISO: fuelReconDateISO(1),
    time: '15:40',
    machine: 'Truck 05',
    operator: 'J. Owusu',
    liters: 400,
    smuHours: 6124.1,
    tank: 'Tank A',
    flag: { tone: 'danger', label: 'Outlier — 2nd full fill this shift' },
    dispenseType: 'MACHINE',
    authStatus: 'AUTHORIZED',
  },
  {
    id: 5,
    dayLabel: 'Yesterday',
    dateISO: fuelReconDateISO(1),
    time: '17:17',
    machine: 'Truck 03',
    operator: 'K. Mensah',
    liters: 160,
    smuHours: 4221.8,
    tank: 'Tank A',
    dispenseType: 'MACHINE',
    authStatus: 'AUTHORIZED',
  },
  {
    id: 13,
    dayLabel: 'Yesterday',
    dateISO: fuelReconDateISO(1),
    time: '07:10',
    machine: 'EXC-01',
    operator: 'Y. Abdulai',
    liters: 640,
    smuHours: 9875,
    tank: 'BOWSER-01 — in-pit',
    dispenseType: 'MACHINE',
    authStatus: 'AUTHORIZED',
  },
  {
    id: 14,
    dayLabel: 'Yesterday',
    dateISO: fuelReconDateISO(1),
    time: '08:25',
    machine: 'DOZ-02',
    operator: 'P. Tetteh',
    liters: 410,
    smuHours: 7206.3,
    tank: 'BOWSER-01 — in-pit',
    dispenseType: 'MACHINE',
    authStatus: 'AUTHORIZED',
  },
  {
    id: 15,
    dayLabel: 'Yesterday',
    dateISO: fuelReconDateISO(1),
    time: '09:40',
    machine: 'WT-01',
    operator: 'E. Osei',
    liters: 260,
    smuHours: 3118.9,
    tank: 'BOWSER-01 — in-pit',
    dispenseType: 'MACHINE',
    authStatus: 'AUTHORIZED',
  },
  {
    id: 6,
    dayLabel: 'Today',
    dateISO: fuelReconDateISO(0),
    time: '06:10',
    machine: 'CAT320',
    operator: 'E. Boateng',
    liters: 210,
    smuHours: 9882.4,
    tank: 'Tank A',
    dispenseType: 'MACHINE',
    authStatus: 'AUTHORIZED',
  },
  {
    id: 7,
    dayLabel: 'Today',
    dateISO: fuelReconDateISO(0),
    time: '06:55',
    machine: 'Truck 03',
    operator: 'K. Mensah',
    liters: 190,
    smuHours: 4233,
    tank: 'Tank A',
    dispenseType: 'MACHINE',
    authStatus: 'AUTHORIZED',
  },
  {
    id: 8,
    dayLabel: 'Yesterday',
    dateISO: fuelReconDateISO(1),
    time: '21:40',
    machine: 'LV-07',
    operator: 'T. Quaye',
    liters: 60,
    smuHours: null,
    tank: 'Tank A',
    dispenseType: 'MACHINE',
    authStatus: 'UNAUTHORIZED',
    flag: { tone: 'danger', label: 'Not on light-vehicle authorized list' },
  },
  {
    id: 9,
    dayLabel: 'Today',
    dateISO: fuelReconDateISO(0),
    time: '07:20',
    machine: 'GEN-01 remote site',
    operator: 'M. Tetteh',
    liters: 400,
    smuHours: null,
    tank: 'Tank A',
    dispenseType: 'DRUM',
    authStatus: 'AUTHORIZED',
    signatureRef: 'DRM-114',
  },
  {
    id: 10,
    dayLabel: 'Today',
    dateISO: fuelReconDateISO(0),
    time: '09:45',
    machine: 'Chainsaw crew',
    operator: 'Y. Asante',
    liters: 20,
    smuHours: null,
    tank: 'Tank A',
    dispenseType: 'DRUM',
    authStatus: 'AUTHORIZED',
    signatureRef: 'DRM-115',
  },
  {
    id: 11,
    dayLabel: '26 Jun',
    dateISO: fuelReconDateISO(9),
    time: '08:30',
    machine: 'Truck 09',
    operator: 'B. Osei',
    liters: 340,
    smuHours: 2211.4,
    tank: 'Tank A',
    dispenseType: 'MACHINE',
    authStatus: 'AUTHORIZED',
    contractor: 'Bulldog Mining Services',
  },
  {
    id: 12,
    dayLabel: '19 Jun',
    dateISO: fuelReconDateISO(16),
    time: '13:15',
    machine: 'Drill 02',
    operator: 'F. Djan',
    liters: 180,
    smuHours: 1874.9,
    tank: 'Tank A',
    dispenseType: 'MACHINE',
    authStatus: 'AUTHORIZED',
    contractor: 'GeoDrill Ltd',
  },
]

const DEMO_BURN_RATES: IFuelOpsBurnRate[] = [
  { machine: 'Truck 03', litersPerEngineHour: 33.9 },
  { machine: 'Truck 05', litersPerEngineHour: 71.2, investigate: true },
  { machine: 'CAT320', litersPerEngineHour: 28.4 },
]

const DEMO_LOSSES: IFuelOpsLossRow[] = [
  {
    id: 1,
    dateLabel: '30 Jun',
    liters: 9000,
    tank: 'Tank A — Main Diesel',
    classification: 'Residual · Operational · Batch Close',
    severity: 'critical',
    owner: 'S. Banda',
    status: 'Under investigation',
    note: 'Full batch auto-classified as residual at close — pattern consistent with meter bypass or booking error. Investigation opened automatically.',
    caseStatus: 'PENDING_APPROVAL',
    outcome: null,
    approval: { requiredAboveGhs: FUEL_WRITE_OFF_APPROVAL_THRESHOLD_GHS, approverRole: 'GM' },
  },
  {
    id: 2,
    dateLabel: '26 Jun',
    liters: 140,
    tank: 'Tank A — Main Diesel',
    classification: 'Spill · Operational · Hot Refuel',
    severity: 'warning',
    owner: 'K. Mensah',
    status: 'Explained',
    caseStatus: 'OPEN',
    outcome: null,
  },
  {
    id: 3,
    dateLabel: '18 Jun',
    liters: 60,
    tank: 'Tank B — Genset',
    classification: 'Evaporation · Operational · Stock Count',
    severity: 'warning',
    owner: 'S. Banda',
    status: 'Written off — signed',
    caseStatus: 'CLOSED',
    outcome: 'WRITTEN_OFF',
    closedAt: '19 Jun',
    approval: {
      requiredAboveGhs: FUEL_WRITE_OFF_APPROVAL_THRESHOLD_GHS,
      approverRole: 'GM',
      approvedBy: 'GM',
      approvedAt: '19 Jun',
    },
  },
  {
    id: 4,
    dateLabel: '19 Jun',
    liters: 180,
    tank: 'Tank A — Main Diesel',
    classification: 'Spill · Operational · Workshop',
    severity: 'warning',
    owner: 'E. Boateng',
    status: 'Written off — signed',
    caseStatus: 'CLOSED',
    outcome: 'WRITTEN_OFF',
    closedAt: '21 Jun',
    approval: {
      requiredAboveGhs: FUEL_WRITE_OFF_APPROVAL_THRESHOLD_GHS,
      approverRole: 'GM',
      approvedBy: 'GM',
      approvedAt: '21 Jun',
    },
  },
  {
    id: 5,
    dateLabel: '25 Jun',
    liters: 340,
    tank: 'BOWSER-01 — Service Truck',
    classification: 'Variance · Operational · Bowser Recon',
    severity: 'warning',
    owner: 'S. Banda',
    status: 'Explained',
    note: 'Meter drift found — see dispenser calibration log.',
    caseStatus: 'CLOSED',
    outcome: 'RECOVERED',
    closedAt: '28 Jun',
  },
]

const DEMO_SUPPLIERS: IFuelOpsSupplierRow[] = [
  {
    id: 9001,
    name: 'Sabel Fuels',
    phoneNumber: '+233 24 555 0192',
    email: 'orders@sabelfuels.com',
    address: 'Plot 14, Heavy Industrial Area, Tarkwa',
    vatId: 'GHA-745-2201',
  },
  {
    id: 9002,
    name: 'GOIL Bulk',
    phoneNumber: '+233 30 221 4478',
    email: 'bulk@goil.com.gh',
    address: 'GOIL House, Kwame Nkrumah Ave, Accra',
    vatId: 'GHA-118-9034',
  },
]

const DEMO_SUPPLIER_COMMERCIALS: IFuelSupplierCommercials[] = [
  {
    supplierName: 'Sabel Fuels',
    lastPriceGhs: 12.4,
    priceTrendLabel: '▲ +3% vs May',
    priceTrendUp: true,
    avgLeadTimeDays: 2.1,
    orderedVsReceivedPct: -0.4,
  },
  {
    supplierName: 'GOIL Bulk',
    lastPriceGhs: 12.65,
    priceTrendLabel: '▲ +2% vs May',
    priceTrendUp: true,
    avgLeadTimeDays: 3.4,
    orderedVsReceivedPct: -1.1,
  },
]

const DEMO_SUPPLIER_CLAIMS: IFuelSupplierClaim[] = [
  {
    id: 1,
    raisedLabel: '24 Jun',
    supplier: 'Sabel Fuels',
    deliveryRef: 'WB-1988',
    litresShort: -850,
    valueGhs: 10498,
    ageDays: 10,
    status: 'OPEN',
    note: 'Seal S-4403 mismatch · dip-verified short',
  },
  {
    id: 2,
    raisedLabel: '12 Apr',
    supplier: 'GOIL Bulk',
    deliveryRef: 'WB-1602',
    litresShort: -300,
    valueGhs: 3630,
    ageDays: 18,
    status: 'SETTLED',
    note: 'Credit note CN-118 received 30 Apr',
  },
  {
    id: 3,
    raisedLabel: '3 Mar',
    supplier: 'Sabel Fuels',
    deliveryRef: 'WB-1493',
    litresShort: -120,
    valueGhs: 1452,
    ageDays: 6,
    status: 'REJECTED',
    note: 'Within 0.5% tolerance on re-measure',
  },
]

const DEMO_AUTHORIZED_DRAWERS: IFuelAuthorizedDrawerGroup[] = [
  {
    machineClass: 'Haul trucks & mobile plant',
    drawers: [
      'K. Mensah',
      'J. Owusu',
      'E. Boateng',
      'S. Adjei',
      'B. Osei',
      'F. Djan',
      'Y. Abdulai',
      'P. Tetteh',
      'E. Osei',
    ],
    updatedLabel: 'Updated 28 Jun · Fuel manager',
  },
  {
    machineClass: 'Light vehicles',
    drawers: ['A. Nkrumah', 'D. Ofori', 'R. Ampofo'],
    updatedLabel: 'Updated 28 Jun · Fuel manager',
  },
  {
    machineClass: 'Drums & static plant',
    drawers: ['S. Banda', 'Storeman (day shift)'],
    updatedLabel: 'Updated 28 Jun · Fuel manager',
  },
]

const DEMO_DRUM_FLOATS: IFuelDrumFloat[] = [
  {
    id: 1,
    ref: 'DRM-114',
    issuedTo: 'GEN-01 remote site',
    issuedL: 400,
    issuedLabel: '2 Jul · 2×200 L',
    confirmedUsedL: 260,
    confirmedVia: 'GEN-01 runtime log',
    returnedEmptyL: 0,
    floatL: 140,
    status: 'OPEN',
    note: 'Confirm remaining 140 L or return empties',
  },
  {
    id: 2,
    ref: 'DRM-115',
    issuedTo: 'Chainsaw crew',
    issuedL: 20,
    issuedLabel: '3 Jul',
    confirmedUsedL: 20,
    confirmedVia: 'crew supervisor',
    returnedEmptyL: 0,
    floatL: 0,
    status: 'CLOSED',
    note: 'Fully consumed — drum returned',
  },
]

const DEMO_FUEL_ALERTS: IFuelAlertResponse[] = [
  {
    id: 'demo-fuel-alert-1',
    riskLevel: 3,
    priority: 3,
    summary: 'Unauthorized draw — LV-07, 60 L by T. Quaye (not on authorized list)',
    actions: ['Review dispense log'],
    confidence: 1,
    machine: { id: 9107, name: 'LV-07' },
    site: null,
    dispenseRateZscore: null,
    tankLevelMismatch: null,
  },
  {
    id: 'demo-fuel-alert-2',
    riskLevel: 2,
    priority: 2,
    summary: '24 Jun delivery short −850 L — claim raised with Sabel Fuels',
    actions: ['View delivery'],
    confidence: 1,
    machine: null,
    site: null,
    dispenseRateZscore: null,
    tankLevelMismatch: null,
  },
  {
    id: 'demo-fuel-alert-3',
    riskLevel: 2,
    priority: 2,
    summary: 'BOWSER-01 meter calibration overdue 65 days',
    actions: ['Schedule calibration'],
    confidence: 1,
    machine: { id: 9110, name: 'BOWSER-01' },
    site: null,
    dispenseRateZscore: null,
    tankLevelMismatch: null,
  },
]

export const FUEL_DEMO_DELIVERIES: IFuelDeliveryRecord[] = USE_OPS_MOCKS ? DEMO_DELIVERIES : []
export const FUEL_LAST_DELIVERY: IFuelOpsDelivery | null = USE_OPS_MOCKS ? DEMO_DELIVERIES[0] : null
export const FUEL_CONSUMPTION_14D: IFuelOpsConsumptionPoint[] = USE_OPS_MOCKS
  ? DEMO_CONSUMPTION_14D
  : []
export const FUEL_DEMO_DISPENSES: IFuelOpsDispenseRow[] = USE_OPS_MOCKS ? DEMO_DISPENSES : []
export const FUEL_BURN_RATES: IFuelOpsBurnRate[] = USE_OPS_MOCKS ? DEMO_BURN_RATES : []
export const FUEL_DEMO_LOSSES: IFuelOpsLossRow[] = USE_OPS_MOCKS ? DEMO_LOSSES : []
export const FUEL_DEMO_SUPPLIERS: IFuelOpsSupplierRow[] = USE_OPS_MOCKS ? DEMO_SUPPLIERS : []
export const FUEL_DEMO_ALERTS: IFuelAlertResponse[] = USE_OPS_MOCKS ? DEMO_FUEL_ALERTS : []
export const FUEL_DEMO_SUPPLIER_CLAIMS: IFuelSupplierClaim[] = USE_OPS_MOCKS
  ? DEMO_SUPPLIER_CLAIMS
  : []
export const FUEL_AUTHORIZED_DRAWERS: IFuelAuthorizedDrawerGroup[] = USE_OPS_MOCKS
  ? DEMO_AUTHORIZED_DRAWERS
  : []
export const FUEL_DEMO_DRUM_FLOATS: IFuelDrumFloat[] = USE_OPS_MOCKS ? DEMO_DRUM_FLOATS : []

const fuelDemoDateBoundISO = (value: any): string | null => {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : format(date, 'yyyy-MM-dd')
}

export const fuelDemoRowsInRange = <T extends IFuelDatedDemoRow>(
  rows: T[],
  dateFrom: any,
  dateTo: any,
): T[] => {
  const from = fuelDemoDateBoundISO(dateFrom)
  const to = fuelDemoDateBoundISO(dateTo)
  if (!from && !to) return rows
  return rows.filter((row) => {
    if (!row.dateISO) return true
    if (from && row.dateISO < from) return false
    if (to && row.dateISO > to) return false
    return true
  })
}

export const getSupplierOpenClaims = (name: string): IFuelSupplierOpenClaimsSummary | null => {
  const open = FUEL_DEMO_SUPPLIER_CLAIMS.filter(
    (claim) => claim.supplier === name && claim.status === 'OPEN',
  )
  if (!open.length) return null
  return {
    count: open.length,
    valueGhs: open.reduce((sum, claim) => sum + claim.valueGhs, 0),
  }
}

export const getSupplierCommercials = (
  name: string,
  index: number,
): IFuelSupplierCommercials | null => {
  if (!USE_OPS_MOCKS) return null
  return (
    DEMO_SUPPLIER_COMMERCIALS.find((c) => c.supplierName === name) ??
    DEMO_SUPPLIER_COMMERCIALS[index % DEMO_SUPPLIER_COMMERCIALS.length]
  )
}

export const fuelLossCostGhs = (liters: number): number =>
  Math.round(liters * FUEL_DIESEL_PRICE_GHS)

export const criticalLossBannerText = (loss: IFuelOpsLossRow): string =>
  `${new Intl.NumberFormat('en-US').format(loss.liters)} L loss under investigation — ${formatGhs(
    fuelLossCostGhs(loss.liters),
  )} · opened ${loss.dateLabel} · owner ${loss.owner}`

export const findOpenCriticalLoss = (rows: IFuelOpsLossRow[]): IFuelOpsLossRow | undefined =>
  rows.find((l) => l.severity === 'critical' && l.status === 'Under investigation')

export const FUEL_DISPENSE_FOOTNOTE =
  'Captured by the task engine’s Fuel Added worker component — no duplicate entry.'

export const FUEL_RESIDUAL_EXPLAINER =
  'Batch-close residuals are auto-flagged: unaccounted remainder is never silently written off.'

export const FUEL_LOSS_CLOSURE_CAPTION =
  'A loss case is not finished until it is closed with an outcome. Write-offs above GH₵ 50,000 need GM sign-off.'

export const FUEL_SUPPLIER_FOOTNOTE = 'Price history per delivery lands with the procurement bridge.'

export const FUEL_CLAIMS_CAPTION = 'A claim nobody tracks is a discount we gave.'

export const FUEL_AUTHORIZED_DRAWERS_CAPTION =
  'Draws by anyone not on the list are flagged UNAUTHORIZED in the log above.'

export const FUEL_DRUM_FLOAT_SUMMARY_SUFFIX =
  'custody in limbo until confirmed or returned.'
